import PropTypes from 'prop-types';
import { Box, Divider, Stack, Typography } from '@mui/material';

const DELIVERY_LABELS = {
  Tbilisi_Delivery: 'Tbilisi delivery',
  Region: 'Region delivery',
  NY_Delivery: 'NY delivery',
  US_Region: 'US region delivery',
};

const AREA_DELIVERIES = ['Tbilisi_Delivery', 'NY_Delivery'];

const amount = (value) => Number(value) || 0;
const usd = (value) => `${value < 0 ? '-' : ''}$${Math.abs(value).toFixed(2)}`;

const Line = ({ label, value, strong, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant={strong ? 'subtitle1' : 'body2'}>{label}</Typography>
    <Typography
      variant={strong ? 'subtitle1' : 'body2'}
      fontWeight={strong ? 'bold' : undefined}
      color={color}
    >
      {value}
    </Typography>
  </Box>
);

Line.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  strong: PropTypes.bool,
  color: PropTypes.string,
};

Line.defaultProps = {
  strong: false,
  color: undefined,
};

const groupLines = ({ group, calculatedPrices: p }) => {
  const discount = amount(p.discountUSD);
  const area = group?.delivery_area?.name;
  return [
    {
      label: 'Transportation',
      value: amount(discount > 0 ? p.originalTransportationUSD : p.transportationUSD),
    },
    { label: 'Discount', value: -discount, color: 'success.main' },
    { label: 'Trump fee (10% of declared value)', value: amount(p.declaredValueFeesUSD) },
    { label: 'Order price', value: amount(p.orderPricesUSD) },
    { label: 'Pickup price', value: amount(p.pickupPriceUSD) },
    ...Object.entries(p.deliveryByTypeUSD ?? { Delivery: p.deliveryUSD }).map(([type, value]) => ({
      label: `${DELIVERY_LABELS[type] ?? type}${area && AREA_DELIVERIES.includes(type) ? ` (${area})` : ''}`,
      value: amount(value),
    })),
    { label: 'Extra charges', value: amount(p.extraChargesUSD) },
  ];
};

const parcelLines = (parcel) => [
  { label: 'Transportation', value: amount(parcel.transportUSD) },
  {
    label: 'Discount',
    value: amount(parcel.transportUSDDiscounted) - amount(parcel.transportUSD),
    color: 'success.main',
  },
  { label: 'Trump fee (10% of declared value)', value: amount(parcel.declaredValueFeeUSD) },
  { label: 'Order price', value: amount(parcel.orderPriceUSD) },
  { label: 'Parcel delivery', value: amount(parcel.deliveryUSD) },
  { label: 'Extra charges', value: amount(parcel.extraUSD) },
];

const PriceBreakdown = ({ prices, orderId }) => {
  if (!prices?.calculatedPrices) return null;

  const parcel = orderId ? prices.ordersCalculated?.find((o) => o.id === orderId) : null;
  if (orderId && !parcel) return null;

  const lines = (parcel ? parcelLines(parcel) : groupLines(prices)).filter((l) => l.value !== 0);
  const totalUSD = parcel ? parcel.finalUSD : prices.calculatedPrices.totalUSD;
  const totalGEL = parcel ? parcel.finalGEL : prices.calculatedPrices.totalGEL;

  return (
    <Stack spacing={0.75}>
      {lines.map((line) => (
        <Line key={line.label} label={line.label} value={usd(line.value)} color={line.color} />
      ))}
      <Divider />
      <Line
        strong
        label={parcel ? 'Parcel total (USD)' : 'Total (USD)'}
        value={usd(amount(totalUSD))}
        color="primary"
      />
      <Line
        strong
        label={parcel ? 'Parcel total (GEL)' : 'Total (GEL)'}
        value={`${amount(totalGEL).toFixed(2)} ₾`}
        color="secondary"
      />
      {parcel && prices.calculatedPrices.deliveryUSD > amount(parcel.deliveryUSD) && (
        <Typography variant="caption" color="text.secondary">
          The group delivery fee is charged once per group, not per parcel.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Exchange rate: {prices.calculatedPrices.exchangeRate}
      </Typography>
    </Stack>
  );
};

PriceBreakdown.propTypes = {
  prices: PropTypes.shape({
    group: PropTypes.object,
    calculatedPrices: PropTypes.object,
    ordersCalculated: PropTypes.array,
  }),
  orderId: PropTypes.string,
};

PriceBreakdown.defaultProps = {
  prices: null,
  orderId: undefined,
};

export default PriceBreakdown;
